import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const SERVICES = [
  "Lawn mowing & edging",
  "Hedge trimming & pruning",
  "Weed control & treatment",
  "Leaf & debris clearance",
  "Soft landscaping",
  "Seasonal tidy-ups",
  "Regular maintenance plans",
  "Other / general enquiry",
];

const empty = { name: "", phone: "", email: "", address: "", service: "", message: "" };

const inputCls =
  "bg-transparent border-0 border-b border-white/20 rounded-none px-0 h-12 text-white placeholder:text-white/30 focus-visible:ring-0 focus-visible:border-brand-orange transition-colors";

export const QuoteForm = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.address || !form.service) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/quotes", form);
      setDone(true);
      setForm(empty);
      toast.success("Quote request sent! We'll be in touch soon.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="relative bg-brand-ink py-24 md:py-36 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-12 bg-brand-orange" />
            <span className="text-xs md:text-sm tracking-[0.25em] uppercase font-heading text-brand-orange">
              Request a quote
            </span>
          </div>
          <h2 className="font-heading font-medium tracking-tight text-white text-4xl md:text-6xl leading-[1.05]">
            Tell us about your grounds.
          </h2>
          <p className="text-white/60 font-body mt-6 max-w-md leading-relaxed">
            Send us a few details and we&apos;ll get back to you with a free, no-obligation quote.
            No online payment needed — just an honest enquiry.
          </p>

          <div className="mt-12 space-y-6">
            {["Free, no-obligation quotes", "Covering the North East of England", "Friendly, reliable local team"].map(
              (t) => (
                <div key={t} className="flex items-center gap-4 text-white/80">
                  <CheckCircle2 size={20} className="text-brand-orange shrink-0" />
                  <span className="font-body">{t}</span>
                </div>
              )
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="bg-black/60 backdrop-blur-xl border border-white/10 p-8 md:p-10"
        >
          {done ? (
            <div data-testid="quote-success" className="py-16 text-center flex flex-col items-center">
              <CheckCircle2 size={56} className="text-brand-orange mb-6" />
              <h3 className="font-heading text-white text-2xl mb-3">Request received</h3>
              <p className="text-white/60 font-body max-w-sm">
                Thanks for getting in touch. The Clarkes GroundCare team will contact you shortly.
              </p>
              <button
                data-testid="quote-another"
                onClick={() => setDone(false)}
                className="mt-8 px-6 py-3 border border-white/20 text-white hover:border-brand-orange hover:text-brand-orange transition-colors"
              >
                Send another request
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="quote-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-white/70 text-xs uppercase tracking-widest">Name *</Label>
                  <Input data-testid="quote-name" value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70 text-xs uppercase tracking-widest">Phone *</Label>
                  <Input data-testid="quote-phone" value={form.phone} onChange={set("phone")} placeholder="07…" className={inputCls} />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-white/70 text-xs uppercase tracking-widest">Email *</Label>
                <Input data-testid="quote-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
              </div>

              <div className="space-y-2">
                <Label className="text-white/70 text-xs uppercase tracking-widest">Address / Area *</Label>
                <Input data-testid="quote-address" value={form.address} onChange={set("address")} placeholder="Town / postcode" className={inputCls} />
              </div>

              <div className="space-y-2">
                <Label className="text-white/70 text-xs uppercase tracking-widest">Service needed *</Label>
                <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                  <SelectTrigger data-testid="quote-service" className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 h-12 text-white focus:ring-0 data-[placeholder]:text-white/30">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-brand-elevated border-white/10 text-white">
                    {SERVICES.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`quote-service-option-${s.replace(/[^a-z]/gi, "").toLowerCase()}`} className="focus:bg-brand-orange focus:text-white">
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-white/70 text-xs uppercase tracking-widest">Message / details</Label>
                <Textarea data-testid="quote-message" value={form.message} onChange={set("message")} placeholder="Tell us what you need…" rows={4} className="bg-transparent border border-white/20 rounded-none text-white placeholder:text-white/30 focus-visible:ring-0 focus-visible:border-brand-orange" />
              </div>

              <button
                type="submit"
                data-testid="quote-submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-3 px-7 py-4 bg-brand-orange text-white font-semibold hover:bg-brand-orangeHover transition-colors disabled:opacity-60"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : null}
                {loading ? "Sending…" : "Send quote request"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
